import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createProgram, getProgram, updateProgram } from "../programsApi";
import { Program } from "../types";
import { FieldSection, SelectField, TextAreaField, TextField } from "../components/FormField";
import StringListEditor from "../components/StringListEditor";
import ObjectListEditor from "../components/ObjectListEditor";
import RequirementGroupsEditor from "../components/RequirementGroupsEditor";
import FeeTablesEditor from "../components/FeeTablesEditor";
import ImageManager from "../components/ImageManager";

const emptyProgram: Program = {
  id: "",
  slug: "",
  status: "draft",
  programType: "passport",
  country: "",
  countryEn: "",
  programTitle: "",
  flagColor: "",
  heroBadge: "",
  heroDescription: "",
  heroHighlights: [],
  investmentAmount: "",
  durationLabel: "",
  trustStats: [],
  overviewStats: [],
  overviewDescription: [],
  advantages: [],
  requirementGroups: [],
  investmentTitle: "",
  investmentNote: "",
  investmentTiers: [],
  feeTables: [],
  processDurationLabel: "",
  processSteps: [],
  ctaTitle: "",
  ctaDescription: "",
  createdAt: "",
  updatedAt: "",
  images: [],
};

export default function AdminProgramEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const navigate = useNavigate();

  const [program, setProgram] = useState<Program>(emptyProgram);
  // A snapshot of the last-loaded/last-saved state -- compared against the
  // live `program` to show a "(未保存)" indicator instead of leaving edits
  // silently unmarked until the admin remembers to click 保存.
  const [savedProgram, setSavedProgram] = useState<Program>(emptyProgram);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew || !id) return;
    getProgram(id)
      .then((p) => {
        setProgram(p);
        setSavedProgram(p);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "加载失败"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const isDirty = JSON.stringify(program) !== JSON.stringify(savedProgram);

  function set<K extends keyof Program>(key: K, value: Program[K]) {
    setProgram((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { id: _id, slug, createdAt, updatedAt, images, ...rest } = program;
      if (isNew) {
        const created = await createProgram(slug, rest);
        navigate(`/admin/programs/${created.id}`, { replace: true });
      } else if (id) {
        const updated = await updateProgram(id, { slug, ...rest });
        setProgram(updated);
        setSavedProgram(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-secondary-light">加载中…</p>;

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-secondary-dark flex items-center gap-2.5">
          {isNew ? "新建项目" : `编辑：${program.country || program.slug}`}
          {isDirty && !saving && (
            <span className="text-sm font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
              未保存
            </span>
          )}
        </h1>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/programs")}
            className="text-secondary-light hover:text-secondary-dark text-sm"
          >
            返回列表
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-gold hover:bg-gold-light disabled:opacity-60 text-secondary-dark font-medium rounded-full px-6 py-2 text-sm transition-colors"
          >
            {saving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <FieldSection title="基本信息">
        <div className="grid grid-cols-2 gap-x-6">
          <TextField
            label="Slug（网址标识，如 st-kitts-citizenship）"
            value={program.slug}
            required
            onChange={(v) => set("slug", v)}
          />
          <SelectField
            label="状态"
            value={program.status}
            onChange={(v) => set("status", v)}
            options={[
              { value: "draft", label: "草稿（不在官网显示）" },
              { value: "published", label: "已发布" },
            ]}
          />
          <SelectField
            label="项目类型"
            value={program.programType}
            onChange={(v) => set("programType", v)}
            options={[
              { value: "passport", label: "护照项目" },
              { value: "residence", label: "居留项目" },
            ]}
          />
          <TextField label="国旗色值（可选，如 #b89355）" value={program.flagColor ?? ""} onChange={(v) => set("flagColor", v)} />
          <TextField label="国家（中文）" value={program.country} required onChange={(v) => set("country", v)} />
          <TextField label="国家（英文）" value={program.countryEn} required onChange={(v) => set("countryEn", v)} />
          <TextField label="项目名称" value={program.programTitle} required onChange={(v) => set("programTitle", v)} />
        </div>
      </FieldSection>

      <FieldSection title="首页 Hero">
        <div className="grid grid-cols-2 gap-x-6">
          <TextField label="标签徽章" value={program.heroBadge ?? ""} onChange={(v) => set("heroBadge", v)} />
          <TextField label="投资金额" value={program.investmentAmount ?? ""} onChange={(v) => set("investmentAmount", v)} />
          <TextField label="周期" value={program.durationLabel ?? ""} onChange={(v) => set("durationLabel", v)} />
        </div>
        <TextAreaField label="项目简介" value={program.heroDescription ?? ""} onChange={(v) => set("heroDescription", v)} />
        <StringListEditor
          label="项目速览要点"
          values={program.heroHighlights ?? []}
          onChange={(v) => set("heroHighlights", v)}
        />
      </FieldSection>

      <FieldSection title="信任背书条">
        <ObjectListEditor
          label="统计数字"
          values={program.trustStats ?? []}
          fields={[
            { key: "value", label: "数值，如 1984" },
            { key: "label", label: "说明" },
          ]}
          emptyItem={{ value: "", label: "" }}
          onChange={(v) => set("trustStats", v)}
        />
      </FieldSection>

      <FieldSection title="国家概况">
        <ObjectListEditor
          label="概况数据"
          values={program.overviewStats ?? []}
          fields={[
            { key: "label", label: "标签，如 官方语言" },
            { key: "value", label: "数值，如 英语" },
          ]}
          emptyItem={{ label: "", value: "" }}
          onChange={(v) => set("overviewStats", v)}
        />
        <StringListEditor
          label="国家介绍段落"
          values={program.overviewDescription ?? []}
          onChange={(v) => set("overviewDescription", v)}
        />
      </FieldSection>

      <FieldSection title="项目优势">
        <ObjectListEditor
          label="优势条目"
          values={program.advantages ?? []}
          fields={[
            { key: "title", label: "标题" },
            { key: "description", label: "说明", multiline: true },
          ]}
          emptyItem={{ title: "", description: "" }}
          onChange={(v) => set("advantages", v)}
        />
      </FieldSection>

      <FieldSection title="申请条件">
        <RequirementGroupsEditor
          values={program.requirementGroups ?? []}
          onChange={(v) => set("requirementGroups", v)}
        />
      </FieldSection>

      <FieldSection title="投资选项">
        <div className="grid grid-cols-2 gap-x-6">
          <TextField label="投资板块标题" value={program.investmentTitle ?? ""} onChange={(v) => set("investmentTitle", v)} />
        </div>
        <TextAreaField label="投资说明" value={program.investmentNote ?? ""} onChange={(v) => set("investmentNote", v)} />
        <ObjectListEditor
          label="投资档位"
          values={program.investmentTiers ?? []}
          fields={[
            { key: "label", label: "档位说明" },
            { key: "amount", label: "金额" },
          ]}
          emptyItem={{ label: "", amount: "" }}
          onChange={(v) => set("investmentTiers", v)}
        />
        <FeeTablesEditor values={program.feeTables ?? []} onChange={(v) => set("feeTables", v)} />
      </FieldSection>

      <FieldSection title="申请流程">
        <TextField
          label="总周期说明，如 6–8 个月"
          value={program.processDurationLabel ?? ""}
          onChange={(v) => set("processDurationLabel", v)}
        />
        <ObjectListEditor
          label="流程步骤（建议 10 步以内，用于横向流程图）"
          values={program.processSteps ?? []}
          fields={[
            { key: "title", label: "步骤标题" },
            { key: "description", label: "步骤说明" },
          ]}
          emptyItem={{ title: "", description: "" }}
          onChange={(v) => set("processSteps", v)}
        />
      </FieldSection>

      <FieldSection title="联系我们">
        <TextField label="标题" value={program.ctaTitle ?? ""} onChange={(v) => set("ctaTitle", v)} />
        <TextAreaField label="说明" value={program.ctaDescription ?? ""} onChange={(v) => set("ctaDescription", v)} />
      </FieldSection>

      <FieldSection title="图片">
        {isNew ? (
          <p className="text-sm text-secondary-light">请先保存项目，再上传图片。</p>
        ) : (
          <ImageManager
            programId={program.id}
            images={program.images}
            onChange={(images) => set("images", images)}
          />
        )}
      </FieldSection>
    </form>
  );
}
